import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import type { StartVerificationDialogProps } from "./types";

/** New verification starts on the credentials page, where staff confirm saved identity and licenses. */
export function StartVerificationDialog({
  scope,
  onClose,
  onRestoreFocus,
}: StartVerificationDialogProps) {
  const trpc = useTRPC();
  const preparation = useQuery(
    trpc.psv.getVerificationPreparation.queryOptions(scope),
  );
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="max-w-lg"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          onRestoreFocus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Run verification from credentials</DialogTitle>
          <DialogDescription>
            Open this provider’s credentials to select their NPI and confirm
            saved state license details before starting verification.
          </DialogDescription>
        </DialogHeader>
        {preparation.isPending && (
          <p role="status" className="text-sm text-muted-foreground">
            Finding the provider’s credential record…
          </p>
        )}
        {preparation.isError && (
          <div role="alert" className="space-y-2 text-sm text-destructive">
            <p>
              Couldn’t open the provider’s credential record:{" "}
              {preparation.error.message}
            </p>
            <Button
              variant="outline"
              disabled={preparation.isFetching}
              onClick={() => void preparation.refetch()}
            >
              Try again
            </Button>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {preparation.data && (
            <Button asChild>
              <Link
                to="/org/bu/$organizationBusinessUnitId/providers/credentials/$affiliationId"
                params={{
                  organizationBusinessUnitId: scope.organizationBusinessUnitId,
                  affiliationId: preparation.data.affiliationId,
                }}
              >
                Open provider credentials
              </Link>
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
